import '../styles/PageGlobal.css';
import './ClientDetailsEditPage.css';
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ClientService } from '../services/ClientService.js';
import { ContactService } from '../services/ContactService.js'; // 🟢 NOUVEAU : Import du service Contact

export default function ClientDetailsEditPage() {
  const { client_id } = useParams();
  const navigate = useNavigate();

  // 🟢 NOUVEAU : Initialisation des services avec useMemo pour éviter les recréations
  const clientService = useMemo(() => new ClientService(), []);
  const contactService = useMemo(() => new ContactService(), []);

  const [loading, setLoading] = useState(true);

  // 1. État simplifié : uniquement les champs modifiables du client
  const [form, setForm] = useState({
    societe1: '', societe2: '', email: '', telephone: '',
    kbis: '', siret: '', siren: '', tvaIntraComm: ''
  });

  // 2. États pour l'affichage en lecture seule des listes
  const [addresses, setAddresses] = useState([]);
  const [contacts, setContacts] = useState([]);

  const fetchClientData = useCallback(async () => {
    setLoading(true);
    try {
      // A. Récupérer les données principales du client et ses adresses
      const data = await clientService.getClientById(client_id);
      
      setForm({
        societe1: data.societe1 || '',
        societe2: data.societe2 || '',
        email: data.email || '',
        telephone: data.telephone || '',
        kbis: data.kbis || '',
        siret: data.siret || '',
        siren: data.siren || '',
        tvaIntraComm: data.tvaIntraComm || '',
      });
      
      setAddresses(data.addresses || []);
      
      // B. 🟢 NOUVEAU : Récupérer spécifiquement les contacts via le ContactService
      try {
        // Assurez-vous que cette méthode existe dans votre ContactService.js 
        // (ex: getContactsByClientId, getByClientId, etc.)
        const contactsData = await contactService.getContactsByClientId(client_id);
        setContacts(contactsData || []);
      } catch (err) {
        console.warn("Impossible de charger les contacts :", err);
        setContacts([]); // Fallback sécurisé si le service n'est pas encore prêt
      }
      
    } catch (err) {
      console.error("Erreur chargement client:", err);
      alert("Erreur lors du chargement des données du client.");
      navigate("/client_list");
    } finally {
      setLoading(false);
    }
  }, [client_id, navigate, clientService, contactService]);

  useEffect(() => { 
    fetchClientData(); 
  }, [fetchClientData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const payload = {
        societe1: form.societe1, 
        societe2: form.societe2, 
        email: form.email, 
        telephone: form.telephone,
        kbis: form.kbis, 
        siret: form.siret, 
        siren: form.siren,
        tvaIntraComm: form.tvaIntraComm
      };
      
      await clientService.updateClient(client_id, payload);
      alert("Client modifié avec succès !");
      navigate("/client_list");
    } catch (err) {
      console.error(err);
      alert("Erreur: " + (err.message || "Impossible de sauvegarder"));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("⚠️ Confirmer la suppression définitive de ce client ?")) return;
    try {
      await clientService.deleteClient(client_id);
      alert("Client supprimé.");
      navigate("/client_list");
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la suppression : " + err.message);
    }
  };

  if (loading) return <div className="containerMain"><p>Chargement des données…</p></div>;

  return (
    <div className="containerMain client-edit-container">
      <h1 className="client-edit-title">Client : Édition des informations principales</h1>
      
      <div className="client-edit-form">
        {/* --- SECTION MODIFIABLE --- */}
        <fieldset className="client-edit-section">
          <legend>Identité de l'entreprise</legend>
          <div className="client-edit-grid client-edit-grid-2">
            <div className="client-edit-row">
              <label>Société 1 *</label>
              <input name="societe1" value={form.societe1} onChange={handleChange} required />
            </div>
            <div className="client-edit-row">
              <label>Société 2</label>
              <input name="societe2" value={form.societe2} onChange={handleChange} />
            </div>
          </div>
        </fieldset>

        <fieldset className="client-edit-section">
          <legend>Contact principal</legend>
          <div className="client-edit-grid client-edit-grid-2">
            <div className="client-edit-row">
              <label>Email *</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} required />
            </div>
            <div className="client-edit-row">
              <label>Téléphone *</label>
              <input name="telephone" value={form.telephone} onChange={handleChange} required />
            </div>
          </div>
        </fieldset>

        <fieldset className="client-edit-section">
          <legend>Informations administratives</legend>
          <div className="client-edit-grid client-edit-grid-3">
            <div className="client-edit-row"><label>KBIS</label><input name="kbis" value={form.kbis} onChange={handleChange} /></div>
            <div className="client-edit-row"><label>SIRET</label><input name="siret" value={form.siret} onChange={handleChange} maxLength={14} /></div>
            <div className="client-edit-row"><label>SIREN</label><input name="siren" value={form.siren} onChange={handleChange} maxLength={9} /></div>
            <div className="client-edit-row"><label>TVA Intracommunautaire</label><input name="tvaIntraComm" value={form.tvaIntraComm} onChange={handleChange} /></div>
          </div>
        </fieldset>

        {/* --- SECTIONS EN LECTURE SEULE (LISTES) --- */}
        
        <fieldset className="client-edit-section" style={{ backgroundColor: '#f8f9fa' }}>
          <legend>📍 Adresses liées (Gérées séparément)</legend>
          {addresses.length === 0 ? (
            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucune adresse enregistrée pour ce client.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {addresses.map((addr, index) => (
                <li key={addr.id || index} style={{ 
                  padding: '12px', 
                  marginBottom: '8px', 
                  background: '#fff', 
                  border: '1px solid #ddd', 
                  borderRadius: '6px' 
                }}>
                  <div style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '4px' }}>
                    {addr.type === 'LIVRAISON' ? '🚚 Livraison' : 
                     addr.type === 'FACTURATION' ? '📄 Facturation' : 
                     addr.type === 'SIEGE_SOCIAL' ? '🏢 Siège Social' : '📍 Adresse'}
                  </div>
                  <div>{addr.adresse1} {addr.adresse2}</div>
                  <div>{addr.codePostal} {addr.ville}, {addr.pays}</div>
                </li>
              ))}
            </ul>
          )}
        </fieldset>

        <fieldset className="client-edit-section" style={{ backgroundColor: '#f8f9fa' }}>
          <legend>👥 Contacts liés</legend>
          {contacts.length === 0 ? (
            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucun contact secondaire enregistré pour ce client.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                  <th style={{ padding: '8px' }}>Nom</th>
                  <th style={{ padding: '8px' }}>Email</th>
                  <th style={{ padding: '8px' }}>Téléphone</th>
                  <th style={{ padding: '8px' }}>Fonction</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact, index) => (
                  <tr key={contact.id || index} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px' }}>{contact.nom || contact.name || 'N/A'}</td>
                    <td style={{ padding: '8px' }}>{contact.email || 'N/A'}</td>
                    <td style={{ padding: '8px' }}>{contact.tel || contact.telephone || 'N/A'}</td>
                    <td style={{ padding: '8px' }}>{contact.fonction || contact.role || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </fieldset>
      </div>

      <div className="client-edit-footer">
        <button className="client-btn client-btn-blue" onClick={handleSave}>Enregistrer les modifications</button>
        <button className="client-btn client-btn-grey" onClick={() => navigate("/client_list")}>Annuler</button>
        <button className="client-btn client-btn-red" onClick={handleDelete}>Supprimer ce client</button>
      </div>
    </div>
  );
}